import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Container, Nav, Tab, Button, Spinner } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import PageLoading from "components/PageLoading";
import NotificationToast from "../../../../components/NotificationToast/NotificationToast";
import AnalysisActions from "../ComponentsAnalysis/AnalysisActions/AnalysisActions";
import SolicitacaoAdequacaoModal from "../../Adequacao/ComponentsAdequacao/SolicitacaoAdequacaoBox";
import AdequacaoModalConfirmacao from "../../Adequacao/ComponentsAdequacao/AdequacaoModalConfirmacao";
import LiberarSolicitacaoModal from "../../Adequacao/ComponentsAdequacao/LiberarSolicitacaoModal";
import type { ItemNaoConforme } from "../../Adequacao/ComponentsAdequacao/SolicitacaoAdequacaoBox";
import IndiceCriterios from "../../../../components/IndiceCriterios/IndiceCriterios";
import CadastroTabV2 from "../../Shipping/V2/tabs/CadastroTabV2";
import DocumentosTabV2 from "../../Shipping/V2/tabs/DocumentosTabV2";
import "../../Shipping/V1/ShippingV1.css";
import "../../Shipping/V1/Tabs/DocObrigatoriosTab/DocObrigatoriosTab.css";
import {
  buildDestaqueAlteracaoAnalistaPorSecaoId,
  buildIndiceItems,
  mapAdequacaoAnalisePorSecaoId,
  type IndiceLink,
  type SecaoDocumento,
} from "../../../../domain/shipping";
import { mapSolicitacaoDetalheToSecoes } from "../../../../domain/shipping/mappers";
import {
  getAvaliacaoSolicitacoesByAno,
  getSolicitacaoAnalise,
  getSolicitacaoDetalhe,
  getSalarioMinimo,
  patchSolicitacaoAnalise,
  postSolicitacaoConcluirAnalise,
  postSolicitacaoSolicitarAdequacao,
  postLiberarNovaSolicitacao,
  type SolicitacaoAnalise,
  type SolicitacaoDetalhe,
} from "../../../../services";
import { extractAxiosErrorMessage } from "../../../../services/api";
import { scrollContentAreaToElement } from "../../../../utils/scrollContentArea";
import { PATHS } from "../../../../routes/paths";
import {
  buildAnaliseCamposPorSecaoId,
  buildCiclosHistoricoAdequacaoPorAnaliseKey,
  buildHistoricoAdequacaoPorAnaliseKey,
  mergeAnaliseCamposComApi,
  temAlgumCriterioDevolver,
  titulosCriteriosAnalise,
  type AnaliseCamposPorSecao,
} from "./buildAnaliseCamposPorSecaoId";
import { solicitacaoDetalheFromAnalise } from "./solicitacaoDetalheFromAnalise";
import {
  buildPatchAnaliseSolicitacaoPayload,
  validarAnaliseCamposPreenchidos,
  validarNaoConformesObrigatorios,
} from "./buildPersistAnalisePayload";
import { resolverAnaliseAposPatch } from "./aplicarRespostaPatchAnalise";

const TABS = [
  { id: "cadastro", label: "CADASTRO" },
  { id: "doc-obrigatorios", label: "DOC. OBRIGATÓRIOS" },
  { id: "doc-bonus", label: "DOC. BÔNUS" },
  { id: "doc-bonus-extra", label: "DOC. BÔNUS EXTRA" },
] as const;

type TabId = (typeof TABS)[number]["id"];

const normalizarTexto = (value: unknown): string =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const normalizarChaveStatus = (value: unknown): string =>
  normalizarTexto(value).replace(/\s+/g, " ");

const STATUSES_ANALISE_FINALIZADA = new Set(["analise concluida", "inapto"]);

const isStatusAnaliseConcluida = (status: unknown): boolean => {
  const normalized = normalizarChaveStatus(status);
  if (!normalized) return false;
  return STATUSES_ANALISE_FINALIZADA.has(normalized);
};

const isDataConclusaoValida = (value: unknown): boolean => {
  const raw = String(value ?? "").trim();
  if (!raw) return false;
  const norm = normalizarTexto(raw);
  if (!norm || norm === "null" || norm === "undefined") return false;
  if (/^0000-00-00/.test(raw) || /^0001-01-01/.test(raw)) return false;
  return true;
};

const pickFirstFilledString = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (value == null) continue;
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }
  return null;
};

const DESCRICAO_OBRIGATORIOS =
  "Devem ser inseridas atas das assembleias de prestação de contas e o demonstrativo de resultado dos sindicatos, " +
  "assinados pelo Presidente e Contador e aprovados pelo Conselho Fiscal. " +
  "A documentação deve ser protocolada anualmente, até 30 de junho. " +
  '<a href="https://www.fiemg.com.br/estatuto" target="_blank" rel="noopener noreferrer">Clique aqui para ver o Estatuto</a>.';

const SINDICATO_HEADER_STORAGE_KEY = "sindicatoHeader";

const SolicitacaoAnalisePage = () => {
  const { idSolicitacao } = useParams<{ idSolicitacao: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [detalhe, setDetalhe] = useState<SolicitacaoDetalhe | null>(null);
  const [analise, setAnalise] = useState<SolicitacaoAnalise | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("cadastro");
  const [isIndiceOpen, setIsIndiceOpen] = useState(false);
  const isChangingTabRef = useRef(false);
  const activeTabRef = useRef<TabId>("cadastro");
  const ultimaSolicitacaoAdequacaoIdsRef = useRef<Set<string> | null>(null);

  const [pendenciasObrigatorios, setPendenciasObrigatorios] = useState<
    Record<string, boolean>
  >({});
  const [pendenciasBonus, setPendenciasBonus] = useState<Record<string, boolean>>(
    {},
  );
  const [pendenciasBonusExtra, setPendenciasBonusExtra] = useState<
    Record<string, boolean>
  >({});
  const [analiseCamposMap, setAnaliseCamposMap] = useState<
    Record<string, AnaliseCamposPorSecao>
  >({});
  const [salarioMinimoAnoAnterior, setSalarioMinimoAnoAnterior] = useState<
    number | null
  >(null);
  const [receitaOperacionalReferencia, setReceitaOperacionalReferencia] =
    useState<number | null>(null);
  const [associadosReferencia, setAssociadosReferencia] = useState<number | null>(
    null,
  );
  const [acaoAnaliseLoading, setAcaoAnaliseLoading] = useState(false);
  const [toastAnalise, setToastAnalise] = useState<{
    show: boolean;
    message: string;
    type: string;
  }>({ show: false, message: "", type: "success" });
  const [showSolicitacaoModal, setShowSolicitacaoModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLiberarSolicitacaoModal, setShowLiberarSolicitacaoModal] = useState(false);
  const [itensNaoConformesModalDraft, setItensNaoConformesModalDraft] = useState<
    ItemNaoConforme[]
  >([]);
  const [concluidaViaListaApi, setConcluidaViaListaApi] = useState(false);

  const handleAnaliseCamposChange = useCallback(
    (value: Record<string, AnaliseCamposPorSecao>) => {
      setAnaliseCamposMap((prev) => ({ ...prev, ...value }));
    },
    [],
  );

  const orderedTabs = useMemo<TabId[]>(() => TABS.map((t) => t.id), []);

  const idNum = useMemo(() => {
    const n = parseInt(String(idSolicitacao), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [idSolicitacao]);

  useEffect(() => {
    if (!idNum) {
      setLoading(false);
      setErro("Identificador da solicitação inválido.");
      return;
    }

    let cancel = false;
    (async () => {
      setLoading(true);
      setErro(null);
      try {
        
        const a = await getSolicitacaoAnalise(idNum);
        const d = a ? null : await getSolicitacaoDetalhe(idNum);

        if (cancel) return;

        setAnalise(a);
        setDetalhe(d);

        if (!a && !d) {
          setErro(
            "Não foi possível carregar o detalhe nem a análise desta solicitação. " +
            "Verifique se o backend está disponível (erros 500) ou se esta solicitação existe neste ambiente (404).",
          );
        }
      } catch {
        if (!cancel) {
          setErro("Erro ao carregar dados. Tente novamente.");
          setDetalhe(null);
          setAnalise(null);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [idNum]);

  useEffect(() => {
    if (!idNum) {
      setConcluidaViaListaApi(false);
      return;
    }

    let cancel = false;
    void (async () => {
      try {
        const response = await getAvaliacaoSolicitacoesByAno(null);
        if (cancel) return;
        const solicitacoes = (response?.solicitacoes ?? []) as Array<
          Record<string, unknown>
        >;
        const registro = solicitacoes.find((s) => {
          const sid = Number(s?.solicitacaoId ?? 0);
          const qid = Number(s?.idQuestionarioResposta ?? 0);
          return sid === idNum || qid === idNum;
        });

        const nomeStatus = normalizarTexto(
          (registro?.statusSolicitacaoAvaliacao as { nome?: string } | undefined)
            ?.nome ??
            registro?.statusSolicitacaoNome ??
            registro?.situacaoNome,
        );
        const analiseConcluidaEm = pickFirstFilledString(
          registro?.analiseConcluidaEm,
          registro?.AnaliseConcluidaEm,
        );

        setConcluidaViaListaApi(
          isDataConclusaoValida(analiseConcluidaEm) ||
            isStatusAnaliseConcluida(nomeStatus),
        );
      } catch {
        if (!cancel) setConcluidaViaListaApi(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [idNum]);

  const detalheParaMap = useMemo(() => {
    if (detalhe) return detalhe;
    if (analise) return solicitacaoDetalheFromAnalise(analise);
    return null;
  }, [detalhe, analise]);

  useEffect(() => {
    if (!idNum) return;
    if (loading) return;

    const d = detalheParaMap;
    const sigla = String(d?.sigla ?? "").trim();
    const cnpjRaw = String(d?.cnpj ?? "").trim();

    if (!sigla || !cnpjRaw || !d) {
      try {
        localStorage.removeItem(SINDICATO_HEADER_STORAGE_KEY);
      } catch {}
      window.dispatchEvent(new Event("sindicatoHeaderUpdated"));
      return;
    }

    try {
      localStorage.setItem(
        SINDICATO_HEADER_STORAGE_KEY,
        JSON.stringify({
          id: d.id,
          sigla,
          cnpj: cnpjRaw,
          nome: String(d.dados?.nome ?? ""),
        }),
      );
    } catch {}
    window.dispatchEvent(new Event("sindicatoHeaderUpdated"));
  }, [idNum, loading, detalheParaMap]);

  useEffect(() => {
    return () => {
      try {
        localStorage.removeItem(SINDICATO_HEADER_STORAGE_KEY);
      } catch {}
      window.dispatchEvent(new Event("sindicatoHeaderUpdated"));
    };
  }, []);

  const { obrigatorios, bonus, bonusExtra } = useMemo(() => {
    if (!detalheParaMap) {
      return {
        obrigatorios: [] as ReturnType<
          typeof mapSolicitacaoDetalheToSecoes
        >["obrigatorios"],
        bonus: [] as ReturnType<typeof mapSolicitacaoDetalheToSecoes>["bonus"],
        bonusExtra: [] as ReturnType<
          typeof mapSolicitacaoDetalheToSecoes
        >["bonusExtra"],
      };
    }
    return mapSolicitacaoDetalheToSecoes(detalheParaMap, {
      incluirCriteriosSomenteAnalista: true,
    });
  }, [detalheParaMap]);

  const analiseCamposPorSecaoId = useMemo(
    () => buildAnaliseCamposPorSecaoId(analise),
    [analise],
  );

  useEffect(() => {
    setAnaliseCamposMap((prev) => {
      const merged: Record<string, AnaliseCamposPorSecao> = {};
      const idsSolicitadosNaRodada = ultimaSolicitacaoAdequacaoIdsRef.current;
      Object.entries(analiseCamposPorSecaoId).forEach(([k, v]) => {
        const fromApi = String(v.observacaoAnalista ?? "");
        const statusApi = String(v.status ?? "");
        const statusPrev = String(prev[k]?.status ?? "");
        const manterStatusAnterior =
          idsSolicitadosNaRodada &&
          !idsSolicitadosNaRodada.has(k) &&
          statusApi === "" &&
          (statusPrev === "CONFORME" ||
            statusPrev === "NAO_CONFORME" ||
            statusPrev === "DEVOLVER");
        merged[k] = {
          ...v,
          status: manterStatusAnterior
            ? (statusPrev as AnaliseCamposPorSecao["status"])
            : v.status,
          observacaoAnalista: fromApi.trim() !== "" ? fromApi : "",
        };
      });
      if (idsSolicitadosNaRodada) {
        ultimaSolicitacaoAdequacaoIdsRef.current = null;
      }
      return merged;
    });
  }, [analiseCamposPorSecaoId]);

  const dadosSindicato = detalhe?.dados ?? analise?.dados ?? undefined;
  const nomeStatusAnaliseNormalizado = useMemo(
    () => normalizarTexto(analise?.status?.nome ?? detalhe?.status?.nome),
    [analise?.status?.nome, detalhe?.status?.nome],
  );
  const statusListaNormalizado = useMemo(
    () =>
      normalizarTexto(
        (location.state as { situacao?: string | null } | null)?.situacao,
      ),
    [location.state],
  );
  const conclusaoLista = useMemo(
    () =>
      pickFirstFilledString(
        (location.state as { conclusaoAnalise?: string | null } | null)
          ?.conclusaoAnalise,
      ),
    [location.state],
  );
  const analiseConcluidaEm = useMemo(
    () =>
      pickFirstFilledString(
        (analise as { analiseConcluidaEm?: string | null } | null | undefined)
          ?.analiseConcluidaEm,
        (analise as { AnaliseConcluidaEm?: string | null } | null | undefined)
          ?.AnaliseConcluidaEm,
        (detalhe as { analiseConcluidaEm?: string | null } | null | undefined)
          ?.analiseConcluidaEm,
        (detalhe as { AnaliseConcluidaEm?: string | null } | null | undefined)
          ?.AnaliseConcluidaEm,
      ),
    [analise, detalhe],
  );
  const analiseConcluida = useMemo(() => {
    if (nomeStatusAnaliseNormalizado) {
      return isStatusAnaliseConcluida(nomeStatusAnaliseNormalizado);
    }
    if (concluidaViaListaApi) return true;
    if (isDataConclusaoValida(conclusaoLista)) return true;
    if (isDataConclusaoValida(analiseConcluidaEm)) return true;
    if (isStatusAnaliseConcluida(statusListaNormalizado)) return true;
    return false;
  }, [
    concluidaViaListaApi,
    conclusaoLista,
    analiseConcluidaEm,
    statusListaNormalizado,
    nomeStatusAnaliseNormalizado,
  ]);

  const dadosIndicadoresAnalise = useMemo(
    () =>
      analise
        ? {
          receitasOperacionais: analise.receitasOperacionais ?? [],
          associados: analise.associados ?? [],
          salarioMinimoAnoAnterior,
        }
        : null,
    [analise, salarioMinimoAnoAnterior],
  );

  
  useEffect(() => {
    if (!analise) {
      setSalarioMinimoAnoAnterior(null);
      return;
    }
    if (typeof analise.salarioMinimo === "number") {
      setSalarioMinimoAnoAnterior(analise.salarioMinimo);
      return;
    }
    if (typeof analise.salarioMinimoAnoAnterior === "number") {
      setSalarioMinimoAnoAnterior(analise.salarioMinimoAnoAnterior);
      return;
    }
    const anos = (analise.receitasOperacionais ?? []).map((r) => r.anoReferencia);
    const anoRefCiclo =
      anos.length > 0 ? Math.max(...anos) : new Date().getFullYear();
    let cancel = false;
    void getSalarioMinimo(anoRefCiclo - 1).then((res) => {
      if (!cancel) {
        const v = Number(res?.valor);
        setSalarioMinimoAnoAnterior(Number.isFinite(v) ? v : null);
      }
    });
    return () => {
      cancel = true;
    };
  }, [analise]);

  useEffect(() => {
    if (!obrigatorios.length) return;
    setPendenciasObrigatorios((prev) => {
      const next = { ...prev };
      obrigatorios.forEach((secao: SecaoDocumento) => {
        if (next[secao.id] === undefined) {
          next[secao.id] = true;
        }
      });
      return next;
    });
  }, [obrigatorios]);

  useEffect(() => {
    if (!bonus.length) return;
    setPendenciasBonus((prev) => {
      const next = { ...prev };
      bonus.forEach((secao: SecaoDocumento) => {
        if (next[secao.id] === undefined) {
          next[secao.id] = true;
        }
      });
      return next;
    });
  }, [bonus]);

  useEffect(() => {
    if (!bonusExtra.length) return;
    setPendenciasBonusExtra((prev) => {
      const next = { ...prev };
      bonusExtra.forEach((secao: SecaoDocumento) => {
        if (next[secao.id] === undefined) {
          next[secao.id] = true;
        }
      });
      return next;
    });
  }, [bonusExtra]);

  
  const parecerAnalistaIndicePorCriterioKey = useMemo(() => {
    const m: Record<string, string> = {};
    for (const [k, v] of Object.entries(analiseCamposMap)) {
      if (v && typeof v.status === "string") m[k] = v.status;
    }
    return m;
  }, [analiseCamposMap]);

  const destaqueAlteracaoPorSecaoId = useMemo(
    () =>
      buildDestaqueAlteracaoAnalistaPorSecaoId(
        analise,
        parecerAnalistaIndicePorCriterioKey,
      ),
    [analise, parecerAnalistaIndicePorCriterioKey],
  );

  const todasSecoesLista = useMemo(
    () => [...obrigatorios, ...bonus, ...bonusExtra],
    [obrigatorios, bonus, bonusExtra],
  );

  const getAnaliseKeyFromSecao = useCallback((secao: SecaoDocumento): string => {
    const criterioId = secao.criterioSolicitacaoId;
    return typeof criterioId === "number" && Number.isFinite(criterioId)
      ? String(criterioId)
      : secao.id;
  }, []);

  const {
    solicitadaAoSindicatoPorSecaoId,
    aguardandoRetornoSindicatoPorSecaoId,
    respondidaPeloSindicatoPorSecaoId,
  } = useMemo(
    () =>
      mapAdequacaoAnalisePorSecaoId(
        analise,
        todasSecoesLista,
        parecerAnalistaIndicePorCriterioKey,
      ),
    [analise, todasSecoesLista, parecerAnalistaIndicePorCriterioKey],
  );

  const adequacaoAguardandoRetornoSindicato = useMemo(
    () => Object.values(aguardandoRetornoSindicatoPorSecaoId).some(Boolean),
    [aguardandoRetornoSindicatoPorSecaoId],
  );

  const analisePodeEditar = useMemo(
    () => !analiseConcluida && !adequacaoAguardandoRetornoSindicato,
    [analiseConcluida, adequacaoAguardandoRetornoSindicato],
  );

  const podeLiberar = analiseConcluida && Boolean(analise?.liberarSolicitacao);

  const parecerAnalistaIndicePorSecaoId = useMemo(() => {
    const out: Record<string, string> = {};
    for (const secao of todasSecoesLista) {
      const key = getAnaliseKeyFromSecao(secao);
      out[secao.id] = String(analiseCamposMap[key]?.status ?? "");
    }
    return out;
  }, [todasSecoesLista, getAnaliseKeyFromSecao, analiseCamposMap]);

  const historicoAdequacaoPorAnaliseKey = useMemo(
    () => buildHistoricoAdequacaoPorAnaliseKey(analise),
    [analise],
  );

  const ciclosHistoricoAdequacaoPorAnaliseKey = useMemo(
    () => buildCiclosHistoricoAdequacaoPorAnaliseKey(analise),
    [analise],
  );

  const indiceItems = useMemo(
    () =>
      buildIndiceItems(
        obrigatorios,
        bonus,
        bonusExtra,
        pendenciasObrigatorios,
        pendenciasBonus,
        pendenciasBonusExtra,
        destaqueAlteracaoPorSecaoId,
        solicitadaAoSindicatoPorSecaoId,
        aguardandoRetornoSindicatoPorSecaoId,
        respondidaPeloSindicatoPorSecaoId,
        parecerAnalistaIndicePorSecaoId,
      ),
    [
      obrigatorios,
      bonus,
      bonusExtra,
      pendenciasObrigatorios,
      pendenciasBonus,
      pendenciasBonusExtra,
      destaqueAlteracaoPorSecaoId,
      solicitadaAoSindicatoPorSecaoId,
      aguardandoRetornoSindicatoPorSecaoId,
      respondidaPeloSindicatoPorSecaoId,
      parecerAnalistaIndicePorSecaoId,
    ],
  );

  const indiceTemPendencias = useMemo(() => {
    const grupoTemPendencia = (
      pend: Record<string, boolean>,
      secoes: SecaoDocumento[],
    ) => {
      if (secoes.length === 0) return false;
      return secoes.some((s) => pend[s.id] !== false);
    };
    return (
      grupoTemPendencia(pendenciasObrigatorios, obrigatorios) ||
      grupoTemPendencia(pendenciasBonus, bonus) ||
      grupoTemPendencia(pendenciasBonusExtra, bonusExtra)
    );
  }, [
    pendenciasObrigatorios,
    pendenciasBonus,
    pendenciasBonusExtra,
    obrigatorios,
    bonus,
    bonusExtra,
  ]);

  const indiceTemDestaqueAlteracao = useMemo(() => {
    for (const sec of indiceItems) {
      for (const link of sec.links || []) {
        if (link.isDestaqueAlteracao) return true;
      }
    }
    return false;
  }, [indiceItems]);

  const indiceTemAdequacaoSolicitada = useMemo(() => {
    for (const sec of indiceItems) {
      for (const link of sec.links || []) {
        if (link.isAdequacaoSolicitada) return true;
      }
    }
    return false;
  }, [indiceItems]);

  const indiceTemAdequacaoRespondida = useMemo(() => {
    for (const sec of indiceItems) {
      for (const link of sec.links || []) {
        if (link.isAdequacaoRespondida) return true;
      }
    }
    return false;
  }, [indiceItems]);

  const indiceBotaoComAlerta =
    indiceTemPendencias ||
    indiceTemDestaqueAlteracao ||
    indiceTemAdequacaoSolicitada ||
    indiceTemAdequacaoRespondida;

  const visibleTabIds = useMemo(() => {
    const ids = new Set<TabId>(["cadastro"]);
    if (loading || obrigatorios.length > 0) ids.add("doc-obrigatorios");
    if (loading || bonus.length > 0) ids.add("doc-bonus");
    if (loading || bonusExtra.length > 0) ids.add("doc-bonus-extra");
    return ids;
  }, [loading, obrigatorios.length, bonus.length, bonusExtra.length]);

  useEffect(() => {
    if (!visibleTabIds.has(activeTab)) {
      setActiveTab("cadastro");
    }
  }, [visibleTabIds, activeTab]);

  const visibleTabs = useMemo(
    () => orderedTabs.filter((id) => visibleTabIds.has(id)),
    [orderedTabs, visibleTabIds],
  );
  const currentTabIndex = visibleTabs.indexOf(activeTab);
  const hasPrevTab = currentTabIndex > 0;
  const hasNextTab = currentTabIndex >= 0 && currentTabIndex < visibleTabs.length - 1;

  const aptoProgramaMeritocracia = useMemo(() => {
    const idsObrigatorios = obrigatorios.map(getAnaliseKeyFromSecao);
    if (idsObrigatorios.length === 0) return false;
    return idsObrigatorios.every(
      (id) => analiseCamposMap[id]?.status === "CONFORME",
    );
  }, [obrigatorios, analiseCamposMap, getAnaliseKeyFromSecao]);

  const titulosPorId = useMemo(() => {
    const m: Record<string, string> = {};
    [...obrigatorios, ...bonus, ...bonusExtra].forEach((s) => {
      m[getAnaliseKeyFromSecao(s)] = s.titulo;
    });
    return m;
  }, [obrigatorios, bonus, bonusExtra, getAnaliseKeyFromSecao]);

  const todasSecoesIds = useMemo(
    () =>
      [...obrigatorios, ...bonus, ...bonusExtra].map((s) =>
        getAnaliseKeyFromSecao(s),
      ),
    [obrigatorios, bonus, bonusExtra, getAnaliseKeyFromSecao],
  );

  const quantidadeAssociadosParaPayload = useMemo(() => {
    const arr = analise?.associados ?? [];
    if (!arr.length) return 0;
    const ano = Math.max(...arr.map((x) => x.anoReferencia));
    const qtdApi = arr.find((x) => x.anoReferencia === ano)?.quantidade ?? 0;
    if (
      typeof associadosReferencia === "number" &&
      Number.isFinite(associadosReferencia)
    ) {
      return associadosReferencia;
    }
    return qtdApi;
  }, [analise, associadosReferencia]);

  const receitaOperacionalBase = useMemo(() => {
    const arr = analise?.receitasOperacionais ?? [];
    if (!arr.length) return 0;
    const ano = Math.max(...arr.map((x) => x.anoReferencia));
    return arr.find((x) => x.anoReferencia === ano)?.valor ?? 0;
  }, [analise]);

  useEffect(() => {
    setReceitaOperacionalReferencia(receitaOperacionalBase);
  }, [receitaOperacionalBase]);

  useEffect(() => {
    const arr = analise?.associados ?? [];
    if (!arr.length) {
      setAssociadosReferencia(null);
      return;
    }
    const ano = Math.max(...arr.map((x) => x.anoReferencia));
    const qtd = Number(arr.find((x) => x.anoReferencia === ano)?.quantidade ?? 0);
    setAssociadosReferencia(Number.isFinite(qtd) ? qtd : null);
  }, [analise]);

  const receitaOperacionalParaPayload = useMemo(
    () =>
      typeof receitaOperacionalReferencia === "number" &&
        Number.isFinite(receitaOperacionalReferencia)
        ? receitaOperacionalReferencia
        : receitaOperacionalBase,
    [receitaOperacionalReferencia, receitaOperacionalBase],
  );

  const podeSolicitarAdequacao = useMemo(
    () =>
      !adequacaoAguardandoRetornoSindicato &&
      todasSecoesIds.some((id) => {
        const st = analiseCamposMap[id]?.status;
        return st === "DEVOLVER";
      }),
    [adequacaoAguardandoRetornoSindicato, todasSecoesIds, analiseCamposMap],
  );

  const finalizarDesabilitadoPorCriterioDevolver = useMemo(() => {
    if (!analise) return false;
    const mapEfetivo = mergeAnaliseCamposComApi(analise, analiseCamposMap);
    return temAlgumCriterioDevolver(mapEfetivo);
  }, [analise, analiseCamposMap]);

  const buildItensNaoConformesModal = useCallback(
    (
      map: Record<string, AnaliseCamposPorSecao>,
      secoesLista: SecaoDocumento[],
    ): ItemNaoConforme[] => {
      return secoesLista
        .filter((s) => {
          const st = map[getAnaliseKeyFromSecao(s)]?.status;
          return st === "DEVOLVER";
        })
        .map((s, index) => {
          const key = getAnaliseKeyFromSecao(s);
          const idNum = Number(key);
          const observacaoAnalista =
            map[key]?.observacaoAnalista ??
            historicoAdequacaoPorAnaliseKey?.[key]?.observacaoAnalista ??
            "";
          return {
            id: Number.isFinite(idNum) ? idNum : index + 1,
            idCampoJson: key,
            conforme: false,
            obsAnalista: observacaoAnalista,
            observacao: observacaoAnalista,
            dataSolicitacao: null,
            adequacaoRealizada: false,
            selecionado: false,
          };
        });
    },
    [getAnaliseKeyFromSecao, historicoAdequacaoPorAnaliseKey],
  );

  const itensNaoConformesModal = useMemo((): ItemNaoConforme[] => {
    const todasSecoes = [...obrigatorios, ...bonus, ...bonusExtra];
    return buildItensNaoConformesModal(analiseCamposMap, todasSecoes);
  }, [obrigatorios, bonus, bonusExtra, analiseCamposMap, buildItensNaoConformesModal]);

  const refetchAnaliseEDetalhe = useCallback(async () => {
    if (!idNum) return;
    const a = await getSolicitacaoAnalise(idNum);
    const d = a ? null : await getSolicitacaoDetalhe(idNum);
    setAnalise(a);
    setDetalhe(d);
  }, [idNum]);

  
  const consolidarAnaliseAposPatch = useCallback(
    async (
      anterior: SolicitacaoAnalise | null,
      patchData: unknown,
    ): Promise<SolicitacaoAnalise | null> => {
      const merged = resolverAnaliseAposPatch(anterior, patchData);
      if (merged?.grupos?.length) return merged;
      if (!idNum) return anterior;
      const fresh = await getSolicitacaoAnalise(idNum);
      if (fresh && !fresh.status && anterior?.status) {
        return { ...fresh, status: anterior.status };
      }
      return fresh;
    },
    [idNum],
  );

  const handleSalvarAnalise = useCallback(async () => {
    if (!idNum) return;
    if (analiseConcluida) {
      return {
        show: true as const,
        message: "Não é possível salvar: a análise já está concluída.",
        type: "error",
      };
    }
    if (adequacaoAguardandoRetornoSindicato) {
      return {
        show: true as const,
        message:
          "Não é possível salvar enquanto houver adequação aguardando retorno do sindicato.",
        type: "error",
      };
    }
    const payload = buildPatchAnaliseSolicitacaoPayload(
      mergeAnaliseCamposComApi(analise, analiseCamposMap),
      receitaOperacionalParaPayload,
      quantidadeAssociadosParaPayload,
    );
    try {
      setAcaoAnaliseLoading(true);
      const patchData = await patchSolicitacaoAnalise(idNum, payload);
      const analisePatch = await consolidarAnaliseAposPatch(analise, patchData);
      if (analisePatch?.grupos?.length) {
        setAnalise(analisePatch);
      }
      return {
        show: true as const,
        message: "Análise salva com sucesso.",
        type: "success",
      };
    } catch (err) {
      return {
        show: true as const,
        message:
          extractAxiosErrorMessage(err) ?? "Erro ao salvar análise.",
        type: "error",
      };
    } finally {
      setAcaoAnaliseLoading(false);
    }
  }, [
    idNum,
    analiseConcluida,
    adequacaoAguardandoRetornoSindicato,
    analise,
    analiseCamposMap,
    receitaOperacionalParaPayload,
    quantidadeAssociadosParaPayload,
    consolidarAnaliseAposPatch,
  ]);

  
  const handleFinalizarPersistirAnalise = useCallback(async () => {
    if (!idNum) return;
    if (analiseConcluida) {
      return {
        show: true as const,
        message: "Não é possível finalizar: a análise já está concluída.",
        type: "error" as const,
      };
    }
    const analiseBase: SolicitacaoAnalise | null = analise;
    if (!analiseBase) {
      return {
        show: true as const,
        message:
          "Não foi possível carregar a análise para finalizar. Atualize a página e tente de novo.",
        type: "error" as const,
      };
    }
    const mapEfetivo = mergeAnaliseCamposComApi(analiseBase, analiseCamposMap);
    const titulosVal = titulosCriteriosAnalise(analiseBase, titulosPorId);
    const v = validarAnaliseCamposPreenchidos(
      todasSecoesIds,
      mapEfetivo,
      titulosVal,
    );
    if (v.ok === false) {
      return {
        show: true as const,
        message: v.mensagens.join(" "),
        type: "error" as const,
      };
    }
    if (temAlgumCriterioDevolver(mapEfetivo)) {
      return {
        show: true as const,
        message:
          "Não é possível finalizar enquanto houver critério em DEVOLVER. Use “Solicitar adequação” ou altere o parecer para CONFORME / NÃO CONFORME.",
        type: "error" as const,
      };
    }
    const { aguardandoRetornoSindicatoPorSecaoId } = mapAdequacaoAnalisePorSecaoId(
      analiseBase,
      todasSecoesLista,
      parecerAnalistaIndicePorCriterioKey,
    );
    const aguardandoTitulos = todasSecoesLista
      .filter((s) => aguardandoRetornoSindicatoPorSecaoId[s.id])
      .map((s) => s.titulo);
    if (aguardandoTitulos.length > 0) {
      return {
        show: true as const,
        message: `Há adequação aguardando retorno do sindicato: ${aguardandoTitulos.join("; ")}. Finalize só depois que o sindicato responder.`,
        type: "error" as const,
      };
    }
    const payload = buildPatchAnaliseSolicitacaoPayload(
      mapEfetivo,
      receitaOperacionalParaPayload,
      quantidadeAssociadosParaPayload,
    );
    try {
      setAcaoAnaliseLoading(true);
      const patchData = await patchSolicitacaoAnalise(idNum, payload);
      const analisePosPatch = await consolidarAnaliseAposPatch(
        analiseBase,
        patchData,
      );
      const usarAnalisePatch = Boolean(analisePosPatch?.grupos?.length);
      const mapPosPatchApi = usarAnalisePatch
        ? buildAnaliseCamposPorSecaoId(analisePosPatch!)
        : mapEfetivo;
      const titulosPosPatch = usarAnalisePatch
        ? titulosCriteriosAnalise(analisePosPatch!, titulosPorId)
        : titulosVal;
      const validacaoPosPatch = validarAnaliseCamposPreenchidos(
        todasSecoesIds,
        mapPosPatchApi,
        titulosPosPatch,
      );
      if (validacaoPosPatch.ok === false) {
        const detalhes = validacaoPosPatch.mensagens.join(" ");
        return {
          show: true as const,
          message: `Existem validações de análise pendentes após salvar no backend. ${detalhes}`,
          type: "error" as const,
        };
      }
      if (usarAnalisePatch && analisePosPatch) {
        setAnalise(analisePosPatch);
      }
      return {
        show: true as const,
        message: "Análise salva. Confirme para concluir a análise deste sindicato.",
        type: "success" as const,
      };
    } catch (err) {
      return {
        show: true as const,
        message:
          extractAxiosErrorMessage(err) ?? "Erro ao salvar análise antes de concluir.",
        type: "error" as const,
      };
    } finally {
      setAcaoAnaliseLoading(false);
    }
  }, [
    idNum,
    analiseConcluida,
    analise,
    todasSecoesIds,
    todasSecoesLista,
    analiseCamposMap,
    parecerAnalistaIndicePorCriterioKey,
    titulosPorId,
    receitaOperacionalParaPayload,
    quantidadeAssociadosParaPayload,
    consolidarAnaliseAposPatch,
  ]);

  
  const handleFinalizarConfirmarAnalise = useCallback(async () => {
    if (!idNum) return;
    if (adequacaoAguardandoRetornoSindicato) {
      return {
        show: true as const,
        message:
          "Não é possível concluir a análise enquanto houver adequação aguardando retorno do sindicato.",
        type: "error" as const,
      };
    }
    try {
      setAcaoAnaliseLoading(true);
      await postSolicitacaoConcluirAnalise(idNum);
      window.setTimeout(() => {
        navigate(PATHS.DOC_ANALYSIS, { replace: true });
      }, 1200);
      return {
        show: true as const,
        message: "Análise finalizada com sucesso.",
        type: "success" as const,
      };
    } catch (err) {
      return {
        show: true as const,
        message:
          extractAxiosErrorMessage(err) ?? "Erro ao concluir análise.",
        type: "error" as const,
      };
    } finally {
      setAcaoAnaliseLoading(false);
    }
  }, [idNum, adequacaoAguardandoRetornoSindicato, navigate]);

  
  const handleSolicitarAdequacaoClick = useCallback(() => {
    if (adequacaoAguardandoRetornoSindicato) {
      setToastAnalise({
        show: true,
        message:
          "Já existe adequação aguardando retorno do sindicato. Aguarde o reenvio antes de alterar ou solicitar nova adequação.",
        type: "error",
      });
      return;
    }
    const v = validarNaoConformesObrigatorios(
      todasSecoesIds,
      analiseCamposMap,
      titulosPorId,
    );
    if (v.ok === false) {
      setToastAnalise({
        show: true,
        message: v.mensagens.join(" "),
        type: "error",
      });
      return;
    }
    const snapshotModal = buildItensNaoConformesModal(analiseCamposMap, [
      ...obrigatorios,
      ...bonus,
      ...bonusExtra,
    ]);
    setItensNaoConformesModalDraft(snapshotModal);
    setShowSolicitacaoModal(true);
  }, [
    adequacaoAguardandoRetornoSindicato,
    todasSecoesIds,
    analiseCamposMap,
    titulosPorId,
    buildItensNaoConformesModal,
    obrigatorios,
    bonus,
    bonusExtra,
  ]);

  
  const handleAdequacaoEnviarESelecao = useCallback(
    async (selecionados: ItemNaoConforme[]) => {
      if (!idNum) return;
      const ids = new Set(
        selecionados
          .map((x) => String(x.idCampoJson ?? ""))
          .filter(Boolean),
      );
      if (ids.size === 0) {
        setToastAnalise({
          show: true,
          message: "Nenhum item selecionado.",
          type: "error",
        });
        return;
      }
      const mapComObservacoesModal = {
        ...mergeAnaliseCamposComApi(analise, analiseCamposMap),
      };
      selecionados.forEach((item) => {
        const key = String(item.idCampoJson ?? "").trim();
        if (!key) return;
        const atual = mapComObservacoesModal[key];
        if (!atual) return;
        mapComObservacoesModal[key] = {
          ...atual,
          observacaoAnalista: String(item.observacao ?? "").slice(0, 10000),
        };
      });
      const mapParaPatchCompleto = { ...mapComObservacoesModal };
      Object.entries(mapParaPatchCompleto).forEach(([key, campos]) => {
        if (!campos || campos.status !== "DEVOLVER" || ids.has(key)) return;
        mapParaPatchCompleto[key] = {
          ...campos,
          status: analiseCamposPorSecaoId[key]?.status ?? "",
        };
      });
      const payload = buildPatchAnaliseSolicitacaoPayload(
        mapParaPatchCompleto,
        receitaOperacionalParaPayload,
        quantidadeAssociadosParaPayload,
      );
      try {
        setAcaoAnaliseLoading(true);
        const patchData = await patchSolicitacaoAnalise(idNum, payload);
        const proxima = await consolidarAnaliseAposPatch(analise, patchData);
        if (proxima?.grupos?.length) {
          setAnalise(proxima);
          setAnaliseCamposMap(
            mergeAnaliseCamposComApi(proxima, mapParaPatchCompleto),
          );
        } else {
          setAnaliseCamposMap(mapParaPatchCompleto);
        }
        ultimaSolicitacaoAdequacaoIdsRef.current = new Set(ids);
        setShowSolicitacaoModal(false);
        setShowConfirmModal(true);
      } catch (err) {
        setToastAnalise({
          show: true,
          message:
            extractAxiosErrorMessage(err) ??
            "Erro ao salvar análise para solicitação de adequação.",
          type: "error",
        });
      } finally {
        setAcaoAnaliseLoading(false);
      }
    },
    [
      idNum,
      analise,
      analiseCamposMap,
      receitaOperacionalParaPayload,
      quantidadeAssociadosParaPayload,
      consolidarAnaliseAposPatch,
      analiseCamposPorSecaoId,
    ],
  );

  
  const handleConfirmarSolicitacaoAdequacao = useCallback(async () => {
    if (!idNum) return;
    try {
      setAcaoAnaliseLoading(true);
      await postSolicitacaoSolicitarAdequacao(idNum);
      await refetchAnaliseEDetalhe();
      setShowConfirmModal(false);
      setToastAnalise({
        show: true,
        message: "Solicitação de adequação enviada com sucesso.",
        type: "success",
      });
      setItensNaoConformesModalDraft([]);
    } catch (err) {
      setToastAnalise({
        show: true,
        message:
          extractAxiosErrorMessage(err) ??
          "Erro ao enviar solicitação de adequação.",
        type: "error",
      });
    } finally {
      setAcaoAnaliseLoading(false);
    }
  }, [idNum, refetchAnaliseEDetalhe]);

  const handleTabChange = useCallback(
    async (nextTab: TabId | null) => {
      if (!nextTab) return;
      if (nextTab === activeTab) return;
      if (isChangingTabRef.current) return;
      isChangingTabRef.current = true;
      try {
        setActiveTab(nextTab);
      } finally {
        isChangingTabRef.current = false;
      }
    },
    [activeTab],
  );

  const goToPrevTab = () => {
    if (!hasPrevTab) return;
    const prevKey = visibleTabs[currentTabIndex - 1];
    if (prevKey) {
      void handleTabChange(prevKey);
    }
  };

  const goToNextTab = () => {
    if (!hasNextTab) return;
    const nextKey = visibleTabs[currentTabIndex + 1];
    if (nextKey) {
      void handleTabChange(nextKey);
    }
  };

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const waitForElementById = useCallback(
    async (
      id: string,
      options?: { maxAttempts?: number; delayMs?: number },
    ): Promise<HTMLElement | null> => {
      const maxAttempts = options?.maxAttempts ?? 12;
      const delayMs = options?.delayMs ?? 100;
      for (let i = 0; i < maxAttempts; i += 1) {
        const el = document.getElementById(id);
        if (el) return el;
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, delayMs);
        });
      }
      return null;
    },
    [],
  );

  const handleIndiceNavigate = useCallback(
    (link: { path?: string; tabKey?: string; id?: string }) => {
      const l = link as IndiceLink;
      if (!l?.id) return;

      setIsIndiceOpen(false);

      void (async () => {
        const tabKey = (l.tabKey as TabId | undefined) ?? undefined;

        if (tabKey && tabKey !== activeTabRef.current) {
          await handleTabChange(tabKey);
        }

        const el = await waitForElementById(l.id!);
        if (!el) return;

        const toggle = el.querySelector("button[aria-expanded]") as
          | HTMLButtonElement
          | null;
        if (toggle?.getAttribute("aria-expanded") === "false") {
          toggle.click();
        }

        window.requestAnimationFrame(() => {
          scrollContentAreaToElement(el, { behavior: "smooth" });
        });
      })();
    },
    [handleTabChange, waitForElementById],
  );

  const voltarLista = () => navigate(PATHS.DOC_ANALYSIS);

  if (loading) return <PageLoading minHeight="80vh" />;

  if (!idNum) {
    return (
      <Container fluid className="shipping-container py-4">
        <Alert variant="danger">{erro}</Alert>
      </Container>
    );
  }

  if (!detalheParaMap) {
    return (
      <Container fluid className="shipping-container py-4">
        {erro ? <Alert variant="warning">{erro}</Alert> : null}
        <Alert variant="danger">
          Não foi possível montar a documentação desta solicitação. Se o ambiente
          de API estiver com falha (erro 500) ou o recurso não for encontrado
          (404), os dados não podem ser exibidos até o backend responder
          corretamente.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {acaoAnaliseLoading ? (
        <div
          className="merit-async-loading-overlay"
          aria-busy="true"
          aria-live="polite"
        >
          <Spinner
            animation="border"
            variant="primary"
            className="page-loading__spinner"
          />
        </div>
      ) : null}
      <Container className="shipping-container" style={{ position: "relative" }}>
        <div
          className="merit-voltar-seta"
          title="Voltar para a lista de análise"
          onClick={voltarLista}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              voltarLista();
            }
          }}
          role="button"
          tabIndex={0}
          style={{
            position: "absolute",
            top: "16px",
            right: "15px",
            zIndex: 20,
          }}
        >
          <FaArrowLeft />
        </div>

        <IndiceCriterios
          open={isIndiceOpen}
          onClose={() => setIsIndiceOpen(false)}
          items={indiceItems}
          onNavigate={handleIndiceNavigate}
        />

        <button
          type="button"
          onClick={() => setIsIndiceOpen(true)}
          className="shipping-index-btn shipping-index-btn--floating"
          aria-label={
            indiceBotaoComAlerta
              ? "Abrir índice de critérios. Existem pendências, alterações a rever ou adequação já enviada ao sindicato."
              : "Abrir índice de critérios"
          }
          title={
            indiceBotaoComAlerta
              ? "Índice de critérios — pendências, alterações ou adequação enviada"
              : "Índice de critérios"
          }
        >
          {indiceBotaoComAlerta ? (
            <span className="shipping-index-btn-badge" aria-hidden />
          ) : null}
          <InfoCircle className="shipping-index-btn-icon" aria-hidden size={22} />
        </button>

        <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
          <div className="shipping-tabs-row" data-merit-ic-below>
            <Nav variant="tabs" className="shipping-tabs">
              {TABS.filter((tab) => visibleTabIds.has(tab.id)).map((tab) => (
                <Nav.Item key={tab.id}>
                  <Nav.Link eventKey={tab.id} className="shipping-tab">
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>

          <Tab.Content className="shipping-content">
            <Tab.Pane eventKey="cadastro">
              <CadastroTabV2
                isActive={activeTab === "cadastro"}
                isLoadingDados={false}
                sindicatoDados={dadosSindicato}
                solicitacaoId={idNum}
                readOnly
              />

              <div className="shipping-nav-row">
                <Button
                  type="button"
                  variant="secondary"
                  className="shipping-nav-btn"
                  onClick={goToPrevTab}
                  disabled={!hasPrevTab}
                >
                  ANTERIOR
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className="shipping-nav-btn shipping-nav-btn--next"
                  style={{
                    backgroundColor: "var(--merit-primary)",
                    borderColor: "var(--merit-primary)",
                  }}
                  onClick={goToNextTab}
                  disabled={!hasNextTab}
                >
                  PRÓXIMO
                </Button>
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="doc-obrigatorios">
              <DocumentosTabV2
                isActive={activeTab === "doc-obrigatorios"}
                tipo="obrigatorios"
                tituloAba="DOCUMENTOS OBRIGATÓRIOS"
                descricaoGeral={DESCRICAO_OBRIGATORIOS}
                secoes={obrigatorios}
                solicitacaoId={idNum}
                readOnly
                dadosIndicadoresAnalise={dadosIndicadoresAnalise}
                onStatusChange={setPendenciasObrigatorios}
                analiseCamposPorSecaoId={analiseCamposMap}
                onAnaliseCamposChange={handleAnaliseCamposChange}
                analisePodeEditar={analisePodeEditar}
                receitaOperacionalReferencia={receitaOperacionalReferencia}
                onReceitaOperacionalReferenciaChange={
                  setReceitaOperacionalReferencia
                }
                associadosReferencia={associadosReferencia}
                onAssociadosReferenciaChange={setAssociadosReferencia}
                analiseAguardandoRetornoPorSecaoId={
                  aguardandoRetornoSindicatoPorSecaoId
                }
                analiseBloqueadaPorAdequacaoGlobal={
                  adequacaoAguardandoRetornoSindicato
                }
                historicoAdequacaoPorAnaliseKey={historicoAdequacaoPorAnaliseKey}
                ciclosHistoricoAdequacaoPorAnaliseKey={
                  ciclosHistoricoAdequacaoPorAnaliseKey
                }
              />
              <Container
                className="doc-obrigatorios-container"
                style={{ paddingTop: 0 }}
              >
                <div className="pt-3 analysis-v2-actions-row">
                  <AnalysisActions
                    onSalvar={handleSalvarAnalise}
                    onFinalizarPersistir={handleFinalizarPersistirAnalise}
                    onFinalizarConfirmar={handleFinalizarConfirmarAnalise}
                    onSolicitarAdequacao={handleSolicitarAdequacaoClick}
                    onLiberarNovaSolicitacao={() => setShowLiberarSolicitacaoModal(true)}
                    showSolicitar={podeSolicitarAdequacao}
                    showLiberar={podeLiberar}
                    showEligibilityCheck
                    apto={aptoProgramaMeritocracia}
                    disabled={
                      acaoAnaliseLoading ||
                      analiseConcluida ||
                      adequacaoAguardandoRetornoSindicato
                    }
                    finalizarDisabled={finalizarDesabilitadoPorCriterioDevolver}
                  />
                </div>

                <div className="shipping-nav-row">
                  <Button
                    type="button"
                    variant="secondary"
                    className="shipping-nav-btn"
                    onClick={goToPrevTab}
                    disabled={!hasPrevTab}
                  >
                    ANTERIOR
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="shipping-nav-btn shipping-nav-btn--next"
                    style={{
                      backgroundColor: "var(--merit-primary)",
                      borderColor: "var(--merit-primary)",
                    }}
                    onClick={goToNextTab}
                    disabled={!hasNextTab}
                  >
                    PRÓXIMO
                  </Button>
                </div>
              </Container>
            </Tab.Pane>

            <Tab.Pane eventKey="doc-bonus">
              <DocumentosTabV2
                isActive={activeTab === "doc-bonus"}
                tipo="bonus"
                tituloAba="DOCUMENTOS BÔNUS"
                secoes={bonus}
                solicitacaoId={idNum}
                readOnly
                dadosIndicadoresAnalise={dadosIndicadoresAnalise}
                onStatusChange={setPendenciasBonus}
                analiseCamposPorSecaoId={analiseCamposMap}
                onAnaliseCamposChange={handleAnaliseCamposChange}
                analisePodeEditar={analisePodeEditar}
                receitaOperacionalReferencia={receitaOperacionalReferencia}
                onReceitaOperacionalReferenciaChange={
                  setReceitaOperacionalReferencia
                }
                associadosReferencia={associadosReferencia}
                onAssociadosReferenciaChange={setAssociadosReferencia}
                analiseAguardandoRetornoPorSecaoId={
                  aguardandoRetornoSindicatoPorSecaoId
                }
                analiseBloqueadaPorAdequacaoGlobal={
                  adequacaoAguardandoRetornoSindicato
                }
                historicoAdequacaoPorAnaliseKey={historicoAdequacaoPorAnaliseKey}
                ciclosHistoricoAdequacaoPorAnaliseKey={
                  ciclosHistoricoAdequacaoPorAnaliseKey
                }
              />
              <Container
                className="doc-obrigatorios-container"
                style={{ paddingTop: 0 }}
              >
                <div className="pt-3 analysis-v2-actions-row">
                  <AnalysisActions
                    onSalvar={handleSalvarAnalise}
                    onFinalizarPersistir={handleFinalizarPersistirAnalise}
                    onFinalizarConfirmar={handleFinalizarConfirmarAnalise}
                    onSolicitarAdequacao={handleSolicitarAdequacaoClick}
                    onLiberarNovaSolicitacao={() => setShowLiberarSolicitacaoModal(true)}
                    showSolicitar={podeSolicitarAdequacao}
                    showLiberar={podeLiberar}
                    showEligibilityCheck={false}
                    apto={aptoProgramaMeritocracia}
                    disabled={
                      acaoAnaliseLoading ||
                      analiseConcluida ||
                      adequacaoAguardandoRetornoSindicato
                    }
                    finalizarDisabled={finalizarDesabilitadoPorCriterioDevolver}
                  />
                </div>

                <div className="shipping-nav-row">
                  <Button
                    type="button"
                    variant="secondary"
                    className="shipping-nav-btn"
                    onClick={goToPrevTab}
                    disabled={!hasPrevTab}
                  >
                    ANTERIOR
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="shipping-nav-btn shipping-nav-btn--next"
                    style={{
                      backgroundColor: "var(--merit-primary)",
                      borderColor: "var(--merit-primary)",
                    }}
                    onClick={goToNextTab}
                    disabled={!hasNextTab}
                  >
                    PRÓXIMO
                  </Button>
                </div>
              </Container>
            </Tab.Pane>

            <Tab.Pane eventKey="doc-bonus-extra">
              <DocumentosTabV2
                isActive={activeTab === "doc-bonus-extra"}
                tipo="bonus-extra"
                tituloAba="DOC. BÔNUS EXTRA"
                secoes={bonusExtra}
                solicitacaoId={idNum}
                readOnly
                dadosIndicadoresAnalise={dadosIndicadoresAnalise}
                onStatusChange={setPendenciasBonusExtra}
                analiseCamposPorSecaoId={analiseCamposMap}
                onAnaliseCamposChange={handleAnaliseCamposChange}
                analisePodeEditar={analisePodeEditar}
                receitaOperacionalReferencia={receitaOperacionalReferencia}
                onReceitaOperacionalReferenciaChange={
                  setReceitaOperacionalReferencia
                }
                associadosReferencia={associadosReferencia}
                onAssociadosReferenciaChange={setAssociadosReferencia}
                analiseAguardandoRetornoPorSecaoId={
                  aguardandoRetornoSindicatoPorSecaoId
                }
                analiseBloqueadaPorAdequacaoGlobal={
                  adequacaoAguardandoRetornoSindicato
                }
                historicoAdequacaoPorAnaliseKey={historicoAdequacaoPorAnaliseKey}
                ciclosHistoricoAdequacaoPorAnaliseKey={
                  ciclosHistoricoAdequacaoPorAnaliseKey
                }
              />
              <Container
                className="doc-obrigatorios-container"
                style={{ paddingTop: 0 }}
              >
                <div className="pt-3 analysis-v2-actions-row">
                  <AnalysisActions
                    onSalvar={handleSalvarAnalise}
                    onFinalizarPersistir={handleFinalizarPersistirAnalise}
                    onFinalizarConfirmar={handleFinalizarConfirmarAnalise}
                    onSolicitarAdequacao={handleSolicitarAdequacaoClick}
                    onLiberarNovaSolicitacao={() => setShowLiberarSolicitacaoModal(true)}
                    showSolicitar={podeSolicitarAdequacao}
                    showLiberar={podeLiberar}
                    showEligibilityCheck={false}
                    apto={aptoProgramaMeritocracia}
                    disabled={
                      acaoAnaliseLoading ||
                      analiseConcluida ||
                      adequacaoAguardandoRetornoSindicato
                    }
                    finalizarDisabled={finalizarDesabilitadoPorCriterioDevolver}
                  />
                </div>

                <div className="shipping-nav-row">
                  <Button
                    type="button"
                    variant="secondary"
                    className="shipping-nav-btn"
                    onClick={goToPrevTab}
                    disabled={!hasPrevTab}
                  >
                    ANTERIOR
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="shipping-nav-btn shipping-nav-btn--next"
                    style={{
                      backgroundColor: "var(--merit-primary)",
                      borderColor: "var(--merit-primary)",
                    }}
                    onClick={goToNextTab}
                    disabled={!hasNextTab}
                  >
                    PRÓXIMO
                  </Button>
                </div>
              </Container>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        <SolicitacaoAdequacaoModal
          show={showSolicitacaoModal}
          onClose={() => {
            setShowSolicitacaoModal(false);
            setItensNaoConformesModalDraft([]);
          }}
          onOpenConfirmModal={(selecionados) => {
            void handleAdequacaoEnviarESelecao(selecionados);
          }}
          itensNaoConformes={
            itensNaoConformesModalDraft.length > 0
              ? itensNaoConformesModalDraft
              : itensNaoConformesModal
          }
          titulosValor={titulosPorId}
        />

        <AdequacaoModalConfirmacao
          show={showConfirmModal}
          isLoading={acaoAnaliseLoading}
          onClose={() => {
            setShowConfirmModal(false);
            setShowSolicitacaoModal(true);
          }}
          onConfirm={() => {
            void handleConfirmarSolicitacaoAdequacao();
          }}
        />

        <LiberarSolicitacaoModal
          show={showLiberarSolicitacaoModal}
          solicitacaoId={idNum}
          isLoading={acaoAnaliseLoading}
          onClose={() => setShowLiberarSolicitacaoModal(false)}
          onConfirm={(criterioIds) => {
            void (async () => {
              if (!idNum) return;
              try {
                setAcaoAnaliseLoading(true);
                await postLiberarNovaSolicitacao(idNum, criterioIds);
                setShowLiberarSolicitacaoModal(false);
                setToastAnalise({
                  show: true,
                  message: "Nova solicitação liberada com sucesso.",
                  type: "success",
                });
                window.setTimeout(() => {
                  navigate(PATHS.DOC_ANALYSIS, { replace: true });
                }, 1200);
              } catch (err) {
                setToastAnalise({
                  show: true,
                  message:
                    extractAxiosErrorMessage(err) ??
                    "Erro ao liberar nova solicitação.",
                  type: "error",
                });
              } finally {
                setAcaoAnaliseLoading(false);
              }
            })();
          }}
        />

        <NotificationToast
          show={toastAnalise.show}
          onClose={() => setToastAnalise((p) => ({ ...p, show: false }))}
          message={toastAnalise.message}
          type={toastAnalise.type}
        />
      </Container>
    </>
  );
};

export default SolicitacaoAnalisePage;
